import { useEffect } from 'react';
import posthog from 'posthog-js';

// Helper to check if we're in a development environment
const isDevelopment = () => {
  // Check for common development environments
  return (
    process.env.NODE_ENV === 'development' || 
    process.env.REACT_APP_ENV === 'development' ||
    !process.env.NODE_ENV ||
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('.local')
  );
};

// Helper to sanitize content (collect more data while maintaining minimum necessary privacy)
const sanitizeContent = (content) => {
  if (!content) return null;
  
  // Increased character limit to 500 to capture more content
  if (typeof content === 'string' && content.length > 500) {
    return content.substring(0, 500) + '...';
  }
  
  return content;
};

const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
const apiHost = process.env.REACT_APP_POSTHOG_HOST;

// Don't initialize if in development mode and REACT_APP_ENABLE_ANALYTICS_IN_DEV isn't true
if (isDevelopment() && process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV !== 'true') {
  // Create a mock PostHog instance that logs instead of sending data
  console.log('PostHog disabled in development. Set REACT_APP_ENABLE_ANALYTICS_IN_DEV=true to enable.');
  
  // Create mock methods for all PostHog methods
  const mockMethods = [
    'capture', 'identify', 'register', 'unregister', 'getFeatureFlag',
    'isFeatureEnabled', 'group', 'resetGroups', 'reset', 'debug'
  ];
  
  mockMethods.forEach(method => {
    posthog[method] = (...args) => {
      console.log(`[PostHog Mock] ${method} called with:`, ...args);
      return method === 'isFeatureEnabled' ? false : method === 'getFeatureFlag' ? null : undefined;
    };
  });
} else {
  // Initialize the real PostHog instance
  posthog.init(apiKey, {
    api_host: apiHost,
    // Enable debug mode in development
    debug: isDevelopment(),
    // Enhanced session recording for maximum data collection
    session_recording: {
      enabled: true,
      // Don't mask text content by default for more detailed recordings
      mask_all_text: false,
      // Don't mask input values by default
      mask_all_element_attributes: false,
      // But still protect obvious sensitive fields
      mask_text_selector: 'input[type="password"], .sensitive-data, [data-ph-mask]',
      // Record canvas elements (for charts, drawings, etc.)
      record_canvas: true,
      // Record network requests
      network_recording: {
        enabled: true,
        // TODO: Update this to your actual domain in production
        recording_domains: [window.location.hostname, '*.yourdomain.com'],
      },
      // Capture console logs for debugging
      console_recording: {
        enabled: true,
        // Exclude sensitive console logs if needed
        levels_to_ignore: ['debug'],
      },
    },
    // Capture pageviews, including route changes: this is a single-page app, so
    // `true` only ever recorded the first load and in-app screens went unseen.
    capture_pageview: 'history_change',
    // Capture click events
    capture_pageleave: true,
    // Enhanced autocapture to get more interaction data
    autocapture: {
      // Always enable in production, use env var in development
      enabled: !isDevelopment() || process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === 'true',
      // Capture DOM changes for dynamic elements
      capture_dom_changes: true,
      // Increased events per second
      event_limit: 10,
    },
    // Longer persistence for better user tracking
    persistence: 'localStorage+cookie',
    // Collect user IP information
    disable_session_recording: false,
    // Default opt-in to tracking (you should adjust based on your privacy policy)
    opt_out_capturing_by_default: false,
    // Enable cross-domain tracking
    cross_subdomain_cookie: true,
    // Enable feature flag tracking
    enable_recording_console_log: true,
  });
  
  // Log that PostHog is enabled
  if (isDevelopment()) {
    console.log('PostHog enabled in development mode');
  }

  // ---- Safe wrappers --------------------------------------------------
  // Protect the application from PostHog runtime errors by wrapping
  // commonly-used methods in try/catch. If PostHog ever throws (e.g.
  // network failure, bad payload) we simply log the error and allow the
  // app to continue running.
  const wrapSafe = (methodName) => {
    const original = posthog[methodName];
    if (typeof original !== 'function') return;
    posthog[methodName] = (...args) => {
      try {
        return original.apply(posthog, args);
      } catch (err) {
        // Use console.error to avoid importing a logger in client bundle
        // eslint-disable-next-line no-console
        console.error(`[PostHog] ${methodName} failed:`, err);
        return undefined;
      }
    };
  };

  ['capture', 'identify', 'group', 'register', 'unregister'].forEach(wrapSafe);
}

// Helper function to track task processing events with session IDs
export const trackTaskProcessed = (taskId, taskType, status, properties = {}) => {
  // Only track if not in development or explicitly enabled
  if (isDevelopment() && process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV !== 'true') {
    console.log('[PostHog Mock] trackTaskProcessed:', { taskId, taskType, status, properties });
    return;
  }
  
  // Add timestamp and enhanced metadata
  const enhancedProperties = {
    ...properties,
    tracked_at: new Date().toISOString(),
    browser: navigator.userAgent,
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
  };
  
  posthog.capture('task_processed', {
    task_id: taskId,
    task_type: taskType,
    status: status,
    ...enhancedProperties
  });
};

// Enhanced helper for tracking payment events with more details
export const trackPayment = (paymentMethod, amount, currency, status, additionalDetails = {}) => {
  // Only track if not in development or explicitly enabled
  if (isDevelopment() && process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV !== 'true') {
    console.log('[PostHog Mock] trackPayment:', { paymentMethod, amount, currency, status, additionalDetails });
    return;
  }
  
  // Add timestamp and enhanced metadata
  const enhancedDetails = {
    ...additionalDetails,
    tracked_at: new Date().toISOString(),
    browser: navigator.userAgent,
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
  };
  
  posthog.capture('payment_received', {
    payment_method: paymentMethod,
    amount: amount,
    currency: currency,
    status: status,
    ...enhancedDetails
  });
};

// New helper for tracking user profile with more details
export const trackUserProfile = (userId, userProperties = {}) => {
  // Only track if not in development or explicitly enabled
  if (isDevelopment() && process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV !== 'true') {
    console.log('[PostHog Mock] trackUserProfile:', { userId, userProperties });
    return;
  }
  
  // Identify the user with detailed properties
  posthog.identify(userId, userProperties);
  
  // Optionally capture a profile viewed event
  posthog.capture('profile_viewed', {
    user_id: userId,
    profile_properties: Object.keys(userProperties),
    viewed_at: new Date().toISOString()
  });
};

// Fire result_viewed once per result (report id) per page load, when the report
// UI is actually open. Re-renders and re-opens of the same report don't re-fire.
const viewedResultIds = new Set();
export const useTrackResultViewed = (isOpen, report, taskType, isReportHidden = false) => {
  const reportId = report && (report.id || report._id);
  useEffect(() => {
    if (!isOpen || !reportId || viewedResultIds.has(reportId)) return;
    viewedResultIds.add(reportId);
    try {
      posthog.capture('result_viewed', {
        task_type: taskType,
        report_id: reportId,
        is_report_hidden: !!isReportHidden,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PostHog] result_viewed failed:', err);
    }
  }, [isOpen, reportId, taskType, isReportHidden]);
};

// Required environment variables:
// REACT_APP_POSTHOG_API_KEY=your_api_key_here
// REACT_APP_POSTHOG_HOST=https://us.i.posthog.com
// REACT_APP_ENABLE_ANALYTICS_IN_DEV=false

export default posthog; 